import React, { useEffect, useMemo, useState } from 'react';
import './PlayerDetailPage.css';
import TopBar from '../components/TopBar';
import TeamBadge from '../components/TeamBadge';
import StatChip from '../components/StatChip';
import AppTooltip from '../components/Tooltip';
import {
  buildRunningAverageSeries,
  buildLastFiveSeries,
  getPlayerById,
} from '../firebase/playerService';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  getUpcomingFixturesWithDifficulty,
  getUpcomingCalendarDifficulty,
  formatFixtureDate,
  formatDifficultyValue,
  SERIE_A_SEASON_LABEL,
  FIXTURE_DIFFICULTY_META,
  getDifficultyColor,
} from '../data/serieACalendar';
import {
  ResponsiveContainer,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Legend, Brush, ReferenceLine,
  AreaChart, Area,
  Cell,
} from 'recharts';
import {
  Activity, BarChart3, Flame, TrendingUp, TrendingDown,
  Trophy, Clock3, ArrowLeft, Minus,
  Target, Shield, Star, AlertTriangle,
  ChevronUp, ChevronDown, CalendarDays,
} from 'lucide-react';

const PREVIOUS_SEASON = 2026;
const PRIMARY   = '#7c5cff';
const SECONDARY = '#31d0aa';
const DANGER    = '#f87171';
const WARN      = '#fbbf24';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function v(x)  { return Number(x || 0); }

function fmt(x) {
  const n = Number(x || 0);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function fmtDec(x, d = 2) {
  const n = Number(x || 0);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
}

function formatDateTime(ts) {
  if (!ts) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(Number(ts)));
}

function formatRelativeTime(ts) {
  if (!ts) return 'Mai';
  const m = Math.floor((Date.now() - Number(ts)) / 60000);
  if (m < 1)  return 'Adesso';
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ore fa`;
  return `${Math.floor(h / 24)} giorni fa`;
}

function formatRoleLabel(role) {
  const map = { P: 'Portiere', D: 'Difensore', C: 'Centrocampista', A: 'Attaccante' };
  return map[String(role || '').trim().toUpperCase()] || role || '—';
}

function getRoleColor(role) {
  const map = { P: '#f5c542', D: '#20c55e', C: '#3b82f6', A: '#ef4444' };
  return map[String(role || '').trim().toUpperCase()] || PRIMARY;
}

function getPenaltyAttempts(s = {}) { return v(s.penalties) + v(s.mpenalties); }
function getPenaltyScored(s = {})   { return v(s.penalties); }
function getPenaltyMissed(s = {})   { return v(s.mpenalties); }
function getPenaltySaved(s = {}) {
  return v(s.saves ?? s.penaltiesSaved ?? s.savedPenalties ?? s.penalties_saved ?? 0);
}

function getPlayerProfile(player) {
  if (v(player.goals) >= 12)                             return 'Finalizzatore';
  if (v(player.assists) >= 10)                           return 'Creatore di gioco';
  if (v(player.markavg) >= 6.5 && v(player.caps) >= 15) return 'Affidabile';
  if (player.playmaker)                                  return 'Playmaker';
  return 'Rotazione';
}

// ─── PERFORMANCE INDEX ────────────────────────────────────────────────────────

function buildPerformanceIndex(player) {
  if (!player) return { score: 0, label: 'N/D', components: [] };

  const role      = String(player.role || '').trim().toUpperCase();
  const isGK      = role === 'P';
  const games     = Math.max(v(player.caps), 1);
  const markavg   = v(player.markavg);
  const fmarkavg  = v(player.fmarkavg);
  const goals     = v(player.goals);
  const assists   = v(player.assists);
  const ycards    = v(player.ycards);
  const rcards    = v(player.rcards);
  const owngoals  = v(player.owngoals);
  const penMissed = v(player.mpenalties);
  const penSaved  = v(player.penaltiesSaved || player.savedPenalties);
  const gotgoals  = v(player.gotgoals);

  const gpp       = goals / games;
  const app       = assists / games;
  const discMalus = ycards * 0.3 + rcards * 0.8 + owngoals * 0.6 + penMissed * 0.7;
  const quality   = markavg * 0.6 + fmarkavg * 0.4;

  let keeperBonus = 0, keeperMalus = 0;
  if (isGK) {
    const gcpg  = gotgoals / games;
    keeperBonus = (penSaved / games) * 1.8 + Math.max(0, 1.2 - gcpg) * 2.5;
    keeperMalus = gcpg * 2.2;
  }

  const attackImpact = isGK ? 0 : gpp * 3.2 + app * 2.4;
  let score = quality * 10 + attackImpact * 10 + keeperBonus * 10 - discMalus * 8 - keeperMalus * 10;
  score = Math.round(Math.max(0, Math.min(100, score)) * 10) / 10;

  let label = 'Scarso';
  if (score >= 85)      label = 'Elite';
  else if (score >= 70) label = 'Ottimo';
  else if (score >= 55) label = 'Buono';
  else if (score >= 40) label = 'Discreto';
  else if (score >= 25) label = 'Debole';

  const components = [
    { label: 'Efficienza tecnica (MV + FV)', value: quality.toFixed(2), icon: '/attribute-icon/mv.png', fallbackText: 'ET' },
    { label: 'Impatto offensivo (per partita)', value: isGK ? 'N/A' : (gpp + app).toFixed(2), icon: '/attribute-icon/gol.png', fallbackText: 'IO' },
    { label: 'Disciplina (malus)', value: discMalus.toFixed(2), icon: '/attribute-icon/yellow-card.png', fallbackText: 'DM' },
  ];
  if (isGK) {
    components.push(
      { label: 'Gol subiti per partita', value: (gotgoals / games).toFixed(2), icon: '/attribute-icon/golsubito.png', fallbackText: 'GS' },
      { label: 'Rigori parati', value: penSaved, icon: '/attribute-icon/parati.png', fallbackText: 'RP' },
    );
  } else {
    components.push(
      { label: 'Gol per partita', value: gpp.toFixed(2), icon: '/attribute-icon/gol.png', fallbackText: 'GF' },
      { label: 'Assist per partita', value: app.toFixed(2), icon: '/attribute-icon/assist.png', fallbackText: 'AS' },
    );
  }
  components.push({ label: 'Indice finale', value: score, icon: '/attribute-icon/value.png', fallbackText: 'PI' });

  return { score, label, components };
}

// ─── SEGNALI ──────────────────────────────────────────────────────────────────

function buildSignals(player, prev) {
  if (!player) return [];
  const role  = String(player.role || '').trim().toUpperCase();
  const isGK  = role === 'P';
  const games = Math.max(v(player.caps), 1);
  const sigs  = [];

  if (v(player.caps) >= 28)                       sigs.push({ id: 'starter',   label: 'Titolare fisso',       icon: 'ti-user-check',      type: 'positive' });
  else if (v(player.caps) <= 10 && v(player.caps)) sigs.push({ id: 'rotation', label: 'Rotazione',            icon: 'ti-replace',         type: 'neutral'  });

  if (!isGK) {
    const gpp = v(player.goals)   / games;
    const app = v(player.assists) / games;
    if (v(player.goals) >= 12)      sigs.push({ id: 'bomber',   label: 'Bomber',             icon: 'ti-flame',           type: 'positive' });
    else if (v(player.goals) >= 6)  sigs.push({ id: 'scorer',   label: 'Buon realizzatore',  icon: 'ti-ball-football',   type: 'positive' });
    if (v(player.assists) >= 8)     sigs.push({ id: 'assist',   label: 'Assist man',         icon: 'ti-arrows-split-2',  type: 'positive' });
    if (v(player.penalties) >= 3)   sigs.push({ id: 'pk',       label: 'Rigorista',          icon: 'ti-circle-dot',      type: 'info'     });
    if (gpp + app >= 0.6)           sigs.push({ id: 'impact',   label: 'Alto impatto',       icon: 'ti-bolt',            type: 'positive' });
  } else {
    const gcpg = v(player.gotgoals) / games;
    if (gcpg <= 1.0 && v(player.caps) >= 10) sigs.push({ id: 'solid-gk',  label: 'Portiere solido',  icon: 'ti-shield-check', type: 'positive' });
    if (getPenaltySaved(player) >= 2)         sigs.push({ id: 'pk-stop',   label: 'Para rigori',      icon: 'ti-hand-stop',    type: 'positive' });
    if (gcpg > 2.0 && v(player.caps) >= 10)  sigs.push({ id: 'leaky-gk',  label: 'Difesa porosa',    icon: 'ti-shield-off',   type: 'warning'  });
  }

  if (v(player.rcards) >= 2)      sigs.push({ id: 'red',      label: 'Espulsioni frequenti', icon: 'ti-cards',           type: 'negative' });
  else if (v(player.ycards) >= 6) sigs.push({ id: 'yc-heavy', label: 'Diffidato seriale',    icon: 'ti-alert-triangle',  type: 'warning'  });
  else if (v(player.ycards) >= 3) sigs.push({ id: 'yc-mid',   label: 'Attenzione gialli',    icon: 'ti-alert-triangle',  type: 'warning'  });
  if (v(player.owngoals) >= 2)    sigs.push({ id: 'og',       label: 'Autogol',              icon: 'ti-arrow-back-up',   type: 'negative' });

  if (prev) {
    const fvD = v(player.fmarkavg) - v(prev.fmarkavg);
    const mvD = v(player.markavg)  - v(prev.markavg);
    if (fvD >= 0.3 && mvD >= 0.1)        sigs.push({ id: 'rising',    label: 'In crescita',   icon: 'ti-trending-up',   type: 'positive' });
    else if (fvD <= -0.3 && mvD <= -0.1) sigs.push({ id: 'declining', label: 'Calo di forma', icon: 'ti-trending-down', type: 'warning'  });
  }

  if (player.playmaker)                                sigs.push({ id: 'playmaker', label: 'Playmaker',  icon: 'ti-topology-star-3', type: 'info' });
  if (v(player.markavg) >= 6.5 && v(player.caps) >= 15) sigs.push({ id: 'reliable', label: 'Affidabile', icon: 'ti-star',            type: 'info' });

  return sigs;
}

// ─── FORMA: GIUDIZIO SINTETICO ────────────────────────────────────────────────

function buildFormJudgment(lastFiveData) {
  if (!lastFiveData?.length) return null;
  const fvs    = lastFiveData.map(d => v(d.fmark ?? d.fmarkavg)).filter(x => x > 0);
  if (!fvs.length) return null;
  const avg    = fvs.reduce((a, b) => a + b, 0) / fvs.length;
  const last   = fvs[fvs.length - 1];
  const prev5  = fvs[fvs.length - 2] ?? last;
  const trend  = last - prev5;

  let label, cls, description;
  if (avg >= 7)        { label = 'Fuoco'; cls = 'hot';     description = 'Rendimento eccezionale nelle ultime giornate'; }
  else if (avg >= 6.5) { label = 'In forma'; cls = 'good'; description = 'Rendimento sopra la media, continuità alta'; }
  else if (avg >= 6)   { label = 'Stabile'; cls = 'ok';    description = 'Rendimento nella norma, nessun allarme'; }
  else if (avg >= 5.5) { label = 'Altalenante'; cls = 'warn'; description = 'Rendimento discontinuo, prestazioni irregolari'; }
  else                 { label = 'In crisi'; cls = 'bad';   description = 'Rendimento sotto soglia, da monitorare'; }

  return { label, cls, description, avg, trend };
}

// ─── SERIE GIORNALIERE ────────────────────────────────────────────────────────

function buildSeasonDailySeries(player) {
  if (!player) return [];
  const candidates = [player.dailyStats, player.matchStats, player.matches, player.matchHistory, player.ratingsHistory, player.seasonHistory, player.giornate];
  const raw = candidates.find(x => Array.isArray(x)) || [];

  const fromRaw = raw.map((e, i) => ({
    day:      Number(e.day ?? e.round ?? e.giornata ?? e.matchday ?? e.gameweek ?? i + 1),
    mark:     v(e.markavg ?? e.mark ?? e.mv ?? e.rating ?? 0),
    fmark:    v(e.fmarkavg ?? e.fmark ?? e.fmv ?? e.fantavoto ?? 0),
    goals:    v(e.goals ?? e.goal ?? 0),
    assists:  v(e.assists ?? e.assist ?? 0),
    gotgoals: v(e.gotgoals ?? e.goalsAgainst ?? 0),
    saves:    v(e.saves ?? e.penaltiesSaved ?? e.savedPenalties ?? 0),
  })).sort((a, b) => a.day - b.day);

  if (fromRaw.length) return fromRaw;

  return (buildRunningAverageSeries(player) || []).map((e, i) => ({
    day:      Number(e.day ?? i + 1),
    mark:     v(e.mark ?? e.avgMark ?? e.avg ?? 0),
    fmark:    v(e.fmark ?? e.avgFmark ?? e.fantavoto ?? 0),
    goals:    v(e.goals ?? e.goal ?? 0),
    assists:  v(e.assists ?? e.assist ?? 0),
    gotgoals: v(e.gotgoals ?? e.goalsAgainst ?? 0),
    saves:    v(e.saves ?? e.penaltiesSaved ?? 0),
  })).sort((a, b) => a.day - b.day);
}

function buildPerformanceDistribution(series = []) {
  const bins = [
    { label: '<5',       min: -Infinity, max: 5,   count: 0, color: '#f87171' },
    { label: '5–5.49',   min: 5,         max: 5.5, count: 0, color: '#fb923c' },
    { label: '5.5–5.99', min: 5.5,       max: 6,   count: 0, color: '#fbbf24' },
    { label: '6–6.49',   min: 6,         max: 6.5, count: 0, color: '#34d399' },
    { label: '6.5–6.99', min: 6.5,       max: 7,   count: 0, color: '#60a5fa' },
    { label: '≥7',       min: 7, max: Infinity,    count: 0, color: PRIMARY   },
  ];
  (Array.isArray(series) ? series : []).forEach(item => {
    const mark = v(item?.mark ?? item?.mv ?? item?.markavg ?? 0);
    const b    = bins.find(bin => mark >= bin.min && mark < bin.max);
    if (b) b.count += 1;
  });
  return bins.map(b => ({ range: b.label, count: b.count, color: b.color }));
}

// ─── MICRO COMPONENTI ─────────────────────────────────────────────────────────

function IconBox({ iconSrc, label, fallbackText = '•' }) {
  const [broken, setBroken] = useState(false);
  return (
    <AppTooltip as="div" className="detail-icon-box" content={label}>
      {iconSrc && !broken
        ? <img src={iconSrc} alt={label} loading="lazy" onError={() => setBroken(true)} />
        : <span>{fallbackText}</span>}
    </AppTooltip>
  );
}

function ChartTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" style={{
      background: isDark ? '#0f172a' : '#ffffff',
      color:      isDark ? '#f8fafc'  : '#111827',
    }}>
      <strong className="chart-tooltip__label">{label}</strong>
      {payload.map(item => (
        <div key={item.dataKey} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: item.color }} />
          {item.name}: <strong>{typeof item.value === 'number' ? item.value.toFixed(2) : item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function SyncBadge({ updatedAt, ratingsUpdatedAt, previousSeasonSyncedAt }) {
  const ts = Math.max(Number(updatedAt || 0), Number(ratingsUpdatedAt || 0), Number(previousSeasonSyncedAt || 0));
  return (
    <AppTooltip as="span" className="detail-sync-pill" content={ts ? formatDateTime(ts) : 'Nessun dato'}>
      <Clock3 size={13} />
      <span>{formatRelativeTime(ts)}</span>
    </AppTooltip>
  );
}

function MetricBadge({ icon, LucideIcon, label, value, fallbackText, accent }) {
  return (
    <div className="detail-mini-badge">
      {LucideIcon
        ? <div className="detail-icon-box" style={accent ? { background: `${accent}18`, borderColor: `${accent}30` } : {}}>
            <LucideIcon size={13} style={accent ? { color: accent } : {}} />
          </div>
        : <IconBox iconSrc={icon} label={label} fallbackText={fallbackText || label.slice(0, 2).toUpperCase()} />
      }
      <div>
        <span>{label}</span>
        <strong style={accent ? { color: accent } : {}}>{value}</strong>
      </div>
    </div>
  );
}

// ─── PI LABEL ─────────────────────────────────────────────────────────────────

const PI_META = {
  Elite:    { cls: 'pi-elite',    color: '#4ade80' },
  Ottimo:   { cls: 'pi-ottimo',   color: PRIMARY   },
  Buono:    { cls: 'pi-buono',    color: '#60a5fa' },
  Discreto: { cls: 'pi-discreto', color: WARN      },
  Debole:   { cls: 'pi-debole',   color: '#fb923c' },
  Scarso:   { cls: 'pi-scarso',   color: DANGER    },
  'N/D':    { cls: 'pi-nd',       color: '#94a3b8' },
};

function PIBadge({ label }) {
  const meta = PI_META[label] || PI_META['N/D'];
  return <span className={`poc-pi-label ${meta.cls}`}>{label}</span>;
}

// ─── COMPARISON BAR ───────────────────────────────────────────────────────────

function ComparisonBar({ curr, prev, max, positiveDir = true }) {
  const cPct = Math.min(100, max > 0 ? (curr / max) * 100 : 0);
  const pPct = Math.min(100, max > 0 ? (prev / max) * 100 : 0);
  const up   = curr >= prev;
  return (
    <div className="cmp-bar">
      <div className="cmp-bar__track">
        <div className="cmp-bar__fill cmp-bar__fill--prev" style={{ width: `${pPct}%` }} />
        <div className="cmp-bar__fill cmp-bar__fill--curr" style={{ width: `${cPct}%` }} />
      </div>
      {prev > 0 && (
        <span className={`cmp-bar__delta ${(positiveDir ? up : !up) ? 'delta--up' : 'delta--down'}`}>
          {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {Math.abs(curr - prev).toFixed(2)}
        </span>
      )}
    </div>
  );
}

// ─── SPARKLINE ────────────────────────────────────────────────────────────────

function Sparkline({ data, valueKey = 'fmark', color = PRIMARY }) {
  if (!data?.length) return <span className="spark-empty">—</span>;
  const vals = data.map(d => v(d[valueKey]));
  const max  = Math.max(...vals, 0.1);
  const H = 32, W = data.length * 14;
  const pts  = vals.map((val, i) => {
    const x = (i / Math.max(vals.length - 1, 1)) * (W - 8) + 4;
    const y = H - 4 - ((val / max) * (H - 8));
    return `${x},${y}`;
  }).join(' ');
  const last  = vals[vals.length - 1];
  const prev5 = vals[vals.length - 2] ?? last;
  const trend = last >= prev5 ? color : DANGER;
  return (
    <svg width={W} height={H} className="sparkline" aria-hidden="true">
      <polyline fill="none" stroke={trend} strokeWidth="2" strokeLinejoin="round" points={pts} />
      {vals.map((val, i) => {
        const x = (i / Math.max(vals.length - 1, 1)) * (W - 8) + 4;
        const y = H - 4 - ((val / max) * (H - 8));
        return <circle key={i} cx={x} cy={y} r={i === vals.length - 1 ? 3.5 : 2} fill={i === vals.length - 1 ? trend : 'var(--card)'} stroke={trend} strokeWidth="1.5" />;
      })}
    </svg>
  );
}

// ─── RING SCORE ───────────────────────────────────────────────────────────────

function RingScore({ score, label, size = 120 }) {
  const R    = (size / 2) - 10;
  const C    = 2 * Math.PI * R;
  const pct  = score / 100;
  const meta = PI_META[label] || PI_META['N/D'];
  return (
    <div className="ring-score" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="rgba(127,127,127,.15)" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={meta.color} strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${C * pct} ${C * (1 - pct)}`}
          strokeDashoffset={C * 0.25}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div className="ring-score__inner">
        <strong>{Math.round(score)}</strong>
        <PIBadge label={label} />
      </div>
    </div>
  );
}

// ─── STAT BIG ─────────────────────────────────────────────────────────────────

function StatBig({ label, value, sub, accent }) {
  return (
    <div className="stat-big" style={{ '--accent': accent || PRIMARY }}>
      <span className="stat-big__label">{label}</span>
      <strong className="stat-big__value">{value}</strong>
      {sub && <span className="stat-big__sub">{sub}</span>}
    </div>
  );
}

// ─── METRIC ROW ───────────────────────────────────────────────────────────────

function MetricRow({ label, curr, prev, currFmt, prevFmt, max, positiveDir = true }) {
  return (
    <div className="metric-row">
      <div className="metric-row__label">{label}</div>
      <div className="metric-row__values">
        <span className="metric-row__curr">{currFmt ?? fmtDec(curr)}</span>
        {prev !== null && prev !== undefined && (
          <span className="metric-row__prev">prec. {prevFmt ?? fmtDec(prev)}</span>
        )}
      </div>
      <ComparisonBar curr={curr} prev={prev ?? 0} max={max} positiveDir={positiveDir} />
    </div>
  );
}

// ─── SIGNAL CHIP ──────────────────────────────────────────────────────────────

const SIG_CLS = { positive: 'sig--positive', warning: 'sig--warning', negative: 'sig--negative', neutral: 'sig--neutral', info: 'sig--info' };

function SignalChip({ signal }) {
  return (
    <span className={`sig-chip ${SIG_CLS[signal.type] || 'sig--neutral'}`}>
      <i className={`ti ${signal.icon}`} aria-hidden="true" />
      {signal.label}
    </span>
  );
}

// ─── STAT GROUP CARD ──────────────────────────────────────────────────────────
// Sezione tematica con titolo + grid di valori usata nel tab Statistiche

function StatGroup({ title, icon: Icon, accent, items }) {
  return (
    <div className="stat-group" style={{ '--group-accent': accent || PRIMARY }}>
      <div className="stat-group__head">
        {Icon && <Icon size={15} className="stat-group__icon" />}
        <span>{title}</span>
      </div>
      <div className="stat-group__grid">
        {items.map(item => (
          <div key={item.label} className="stat-group__item">
            <span className="stat-group__item-label">{item.label}</span>
            <strong className="stat-group__item-value" style={item.highlight ? { color: accent || PRIMARY } : {}}>
              {item.value}
            </strong>
            {item.sub && <span className="stat-group__item-sub">{item.sub}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CONFRONTO TABELLA CURR VS PREV ──────────────────────────────────────────
// Usata nel tab Stagione precedente

function CompareTable({ rows }) {
  return (
    <div className="cmp-table">
      <div className="cmp-table__header">
        <span>Metrica</span>
        <span>Corrente</span>
        <span>Precedente</span>
        <span>Δ</span>
      </div>
      {rows.map(row => {
        const curr = Number(row.curr ?? 0);
        const prev = Number(row.prev ?? 0);
        const delta = curr - prev;
        const up    = delta >= 0;
        const sig   = Math.abs(delta) >= 0.01;
        const good  = row.positiveDir !== false ? up : !up;
        return (
          <div key={row.label} className="cmp-table__row">
            <span className="cmp-table__row-label">{row.label}</span>
            <strong className="cmp-table__curr">{row.currFmt ?? fmtDec(curr)}</strong>
            <span className="cmp-table__prev">{row.prevFmt ?? fmtDec(prev)}</span>
            <span className={`cmp-table__delta ${sig ? (good ? 'delta--up' : 'delta--down') : 'delta--flat'}`}>
              {sig
                ? (up ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
                : <Minus size={12} />}
              {sig ? Math.abs(delta).toFixed(2) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── FORMA DAY CARD ───────────────────────────────────────────────────────────

function FormaDayCard({ item, index }) {
  const fv  = v(item.fmark ?? item.fmarkavg);
  const mv  = v(item.mark  ?? item.markavg);
  const hot = fv >= 7;
  const bad = fv < 5.5 && fv > 0;
  const neutral = fv === 0;

  return (
    <div className={`forma-day ${hot ? 'forma-day--hot' : bad ? 'forma-day--bad' : neutral ? 'forma-day--neutral' : 'forma-day--ok'}`}>
      <div className="forma-day__dot" />
      <div className="forma-day__body">
        <div className="forma-day__top">
          <span className="forma-day__round">Giornata {item.day ?? index + 1}</span>
          <strong className="forma-day__fv">{fv > 0 ? fmtDec(fv) : 'S.V.'}</strong>
        </div>
        <div className="forma-day__stats">
          <span>MV {mv > 0 ? fmtDec(mv) : '—'}</span>
          {v(item.goals)   > 0 && <span className="forma-day__event forma-day__event--goal">⚽ {item.goals} gol</span>}
          {v(item.assists) > 0 && <span className="forma-day__event forma-day__event--assist">🅰️ {item.assists} assist</span>}
          {v(item.gotgoals) > 0 && <span className="forma-day__event forma-day__event--danger">× {item.gotgoals} sub.</span>}
          {v(item.saves)   > 0 && <span className="forma-day__event forma-day__event--save">🧤 {item.saves} par.</span>}
        </div>
      </div>
    </div>
  );
}

// ─── PLAYER OVERVIEW CARD ─────────────────────────────────────────────────────

function useIsMobile(query = '(max-width: 640px)') {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isMobile;
}

function PlayerOverviewCard({ player, prev, lastFive }) {
  const isMobile  = useIsMobile();
  const hasPrev   = Boolean(prev?.markavg || prev?.caps);
  const prevData  = hasPrev ? prev : null;
  const pi        = useMemo(() => buildPerformanceIndex(player), [player]);
  const signals   = useMemo(() => buildSignals(player, prevData), [player, prevData]);
  const role      = String(player?.role || '').trim().toUpperCase();
  const isGK      = role === 'P';
  const games     = Math.max(v(player.caps), 1);
  const roleColor = getRoleColor(role);

  const bigStats = useMemo(() => {
    if (!player) return [];
    if (isGK) return [
      { label: 'Media FV',   value: fmtDec(player.fmarkavg), sub: `MV ${fmtDec(player.markavg)}`, accent: PRIMARY   },
      { label: 'Presenze',   value: fmt(player.caps),        sub: 'partite giocate',               accent: SECONDARY },
      { label: 'Gol subiti', value: fmt(player.gotgoals),    sub: `${fmtDec(v(player.gotgoals)/games)} / partita`, accent: DANGER },
    ];
    return [
      { label: 'Media FV', value: fmtDec(player.fmarkavg), sub: `MV ${fmtDec(player.markavg)}`, accent: PRIMARY   },
      { label: 'Gol',      value: fmt(player.goals),       sub: `${fmtDec(v(player.goals)/games)} / partita`,   accent: '#f97316' },
      { label: 'Assist',   value: fmt(player.assists),     sub: `${fmtDec(v(player.assists)/games)} / partita`, accent: SECONDARY },
    ];
  }, [player, isGK, games]);

  const compMetrics = useMemo(() => {
    if (!player) return [];
    const p   = prevData;
    const pg  = Math.max(v(player.caps), 1);
    const ppg = p ? Math.max(v(p.caps), 1) : 1;
    const gpp  = v(player.goals)   / pg;
    const app  = v(player.assists) / pg;
    const pgpp = p ? v(p.goals)   / ppg : null;
    const papp = p ? v(p.assists) / ppg : null;

    if (isGK) {
      const gcpg  = v(player.gotgoals) / pg;
      const pgcpg = p ? v(p.gotgoals) / ppg : null;
      return [
        { label: 'Media voto',        curr: v(player.markavg),  prev: p ? v(p.markavg)  : null, max: 10, currFmt: fmtDec(player.markavg),  prevFmt: p ? fmtDec(p.markavg)  : null },
        { label: 'Media FV',          curr: v(player.fmarkavg), prev: p ? v(p.fmarkavg) : null, max: 10, currFmt: fmtDec(player.fmarkavg), prevFmt: p ? fmtDec(p.fmarkavg) : null },
        { label: 'Presenze',          curr: v(player.caps),     prev: p ? v(p.caps)     : null, max: 38, currFmt: fmt(player.caps),         prevFmt: p ? fmt(p.caps)         : null },
        { label: 'Gol subiti / gara', curr: gcpg, prev: pgcpg,  max: 4, currFmt: fmtDec(gcpg),   prevFmt: pgcpg != null ? fmtDec(pgcpg) : null, positiveDir: false },
      ];
    }
    return [
      { label: 'Media voto',    curr: v(player.markavg),  prev: p ? v(p.markavg)  : null, max: 10, currFmt: fmtDec(player.markavg),  prevFmt: p ? fmtDec(p.markavg)  : null },
      { label: 'Media FV',      curr: v(player.fmarkavg), prev: p ? v(p.fmarkavg) : null, max: 10, currFmt: fmtDec(player.fmarkavg), prevFmt: p ? fmtDec(p.fmarkavg) : null },
      { label: 'Presenze',      curr: v(player.caps),     prev: p ? v(p.caps)     : null, max: 38, currFmt: fmt(player.caps),         prevFmt: p ? fmt(p.caps)         : null },
      { label: 'Gol / partita', curr: gpp,  prev: pgpp,   max: 1,  currFmt: fmtDec(gpp),  prevFmt: pgpp  != null ? fmtDec(pgpp)  : null },
      { label: 'Assist / gara', curr: app,  prev: papp,   max: 0.6, currFmt: fmtDec(app), prevFmt: papp != null ? fmtDec(papp) : null },
    ];
  }, [player, prevData, isGK]);

  return (
    <div className="poc-card glass">
      <div className="poc-header" style={{ '--role-color': roleColor }}>
        <div className="poc-header__left">
          <div className="poc-header__team-wrap">
            <TeamBadge team={player.team} size={52} />
            <div className="poc-role-dot" style={{ background: roleColor }} />
          </div>
          <div>
            <div className="poc-header__meta">
              <span className="poc-pill" style={{ color: roleColor, borderColor: roleColor + '44', background: roleColor + '18' }}>
                {formatRoleLabel(role)}
              </span>
              <SyncBadge updatedAt={player.updatedAt} ratingsUpdatedAt={player.ratingsUpdatedAt} previousSeasonSyncedAt={player.previousSeasonSyncedAt} />
            </div>
            <h2 className="poc-name">{player.name}</h2>
            <p className="poc-profile">{getPlayerProfile(player)}</p>
          </div>
        </div>
        <RingScore score={pi.score} label={pi.label} size={isMobile ? 92 : 130} />
      </div>

      <div className="poc-big-stats">
        {bigStats.map(s => <StatBig key={s.label} {...s} />)}
      </div>

      {signals.length > 0 && (
        <div className="poc-signals">
          {signals.map(s => <SignalChip key={s.id} signal={s} />)}
        </div>
      )}

      <div className="poc-divider" />

      <div className="poc-comparison">
        <div className="poc-section-title">
          Metriche chiave
          {hasPrev && (
            <div className="poc-legend">
              <span className="poc-legend__item poc-legend__item--curr">Corrente</span>
              <span className="poc-legend__item poc-legend__item--prev">Precedente</span>
            </div>
          )}
        </div>
        <div className="poc-metrics-list">
          {compMetrics.map(m => <MetricRow key={m.label} {...m} />)}
        </div>
      </div>

      {lastFive?.length > 0 && (
        <>
          <div className="poc-divider" />
          <div className="poc-forma">
            <div className="poc-section-title">Ultime {lastFive.length} giornate</div>
            <div className="poc-forma__grid">
              {lastFive.map((item, i) => {
                const fv  = v(item.fmark ?? item.fmarkavg);
                const mv  = v(item.mark  ?? item.markavg);
                const hot = fv >= 7;
                const bad = fv < 5.5 && fv > 0;
                return (
                  <div key={item.day ?? i} className={`poc-forma__day ${hot ? 'day--hot' : ''} ${bad ? 'day--bad' : ''}`}>
                    <span className="poc-forma__round">G{item.day ?? i + 1}</span>
                    <strong className="poc-forma__fv">{fv > 0 ? fmtDec(fv) : '—'}</strong>
                    <span className="poc-forma__mv">MV {mv > 0 ? fmtDec(mv) : '—'}</span>
                    {v(item.goals)   > 0 && <span className="poc-forma__event poc-forma__event--goal">{item.goals} ⚽</span>}
                    {v(item.assists) > 0 && <span className="poc-forma__event poc-forma__event--assist">{item.assists} 🅰️</span>}
                  </div>
                );
              })}
              <div className="poc-forma__spark">
                <span className="poc-forma__spark-label">Trend FV</span>
                <Sparkline data={lastFive} valueKey="fmark" color={PRIMARY} />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="poc-divider" />
      <div className="poc-discipline">
        <div className="poc-section-title">Disciplina</div>
        <div className="poc-discipline__grid">
          <div className="poc-disc-item poc-disc-item--yc"><span>{fmt(player.ycards)}</span><small>Ammon.</small></div>
          <div className="poc-disc-item poc-disc-item--rc"><span>{fmt(player.rcards)}</span><small>Espuls.</small></div>
          <div className="poc-disc-item"><span>{fmt(player.owngoals)}</span><small>Autogol</small></div>
          {!isGK && <div className="poc-disc-item"><span>{fmt(player.mpenalties)}</span><small>Rig. sbag.</small></div>}
          {isGK  && <div className="poc-disc-item poc-disc-item--save"><span>{fmt(getPenaltySaved(player))}</span><small>Rig. parati</small></div>}
        </div>
      </div>
    </div>
  );
}

// ─── CALENDARIO: PARTITE ANCORA DA GIOCARE ────────────────────────────────────

function CalendarTab({ player }) {
  const remainingFixtures = useMemo(
    () => getUpcomingFixturesWithDifficulty(player.team, 38),
    [player.team]
  );
  const avgAll = useMemo(
    () => getUpcomingCalendarDifficulty(player.team, 38),
    [player.team]
  );
  const avgNext7 = useMemo(
    () => getUpcomingCalendarDifficulty(player.team, 7),
    [player.team]
  );

  return (
    <section className="detail-grid">
      <article className="detail-card glass detail-card--wide">
        <div className="detail-card__head">
          <div>
            <h2>Partite ancora da giocare</h2>
            <span>Le avversarie che {player.team} deve ancora affrontare, con la difficoltà di ognuna (in casa più facile, in trasferta più dura)</span>
          </div>
          <div className="detail-card__pill"><CalendarDays size={16} /><span>{SERIE_A_SEASON_LABEL}</span></div>
        </div>

        {!remainingFixtures.length ? (
          <div className="detail-empty-inline">
            Nessuna partita in calendario per {player.team}: stagione conclusa o squadra non presente in Serie A.
          </div>
        ) : (
          <>
            <div className="cal-summary">
              <div className="cal-summary__item">
                <span>Partite rimanenti</span>
                <strong>{remainingFixtures.length}</strong>
              </div>
              <div className="cal-summary__item">
                <span>Difficoltà prossime 7</span>
                <strong style={{ color: getDifficultyColor(avgNext7) }}>{fmtDec(avgNext7, 1)} / 5</strong>
              </div>
              <div className="cal-summary__item">
                <span>Difficoltà media totale</span>
                <strong style={{ color: getDifficultyColor(avgAll) }}>{fmtDec(avgAll, 1)} / 5</strong>
              </div>
              <div className="cal-summary__legend">
                {Object.entries(FIXTURE_DIFFICULTY_META).map(([level, meta]) => (
                  <span key={level} className="cal-summary__legend-item">
                    <span className="cal-summary__legend-dot" style={{ background: meta.color }} />
                    {level} · {meta.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="cal-fixtures-grid">
              {remainingFixtures.map((f) => {
                // Difficoltà già corretta per il fattore campo (mezzi punti):
                // colore ed etichetta usano il livello arrotondato 1-5.
                const level = Math.min(5, Math.max(1, Math.round(f.difficulty)));
                const meta = FIXTURE_DIFFICULTY_META[level] || FIXTURE_DIFFICULTY_META[3];
                return (
                  <div key={f.giornata} className="cal-fixture" style={{ '--diff-color': meta.color }}>
                    <div className="cal-fixture__top">
                      <span className="cal-fixture__round">G{f.giornata}</span>
                      <span className="cal-fixture__date">{formatFixtureDate(f.date)}</span>
                    </div>
                    <div className="cal-fixture__opp">
                      <TeamBadge team={f.opponent} size={26} />
                      <strong>{f.opponent}</strong>
                    </div>
                    <div className="cal-fixture__bottom">
                      <span className={`cal-fixture__venue ${f.home ? 'cal-fixture__venue--home' : 'cal-fixture__venue--away'}`}>
                        {f.home ? 'Casa' : 'Trasferta'}
                      </span>
                      <AppTooltip as="span" className="cal-fixture__diff" content={`Difficoltà ${formatDifficultyValue(f.difficulty)}/5 (avversario ${f.baseDifficulty}/5, ${f.home ? 'in casa -0.5' : 'in trasferta +0.5'})`}>
                        {meta.label} · {formatDifficultyValue(f.difficulty)}/5
                      </AppTooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </article>
    </section>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function PlayerDetailPage() {
  const { id }      = useParams();
  const { isDark }  = useTheme();
  const [player,  setPlayer]  = useState(null);
  const [tab,     setTab]     = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPlayerById(id)
      .then(data => { if (!cancelled) { setPlayer(data); setLoading(false); } })
      .catch(()  => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const chartText = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,.10)' : 'rgba(17,24,39,.10)';

  const prev          = player?.previousSeasonStats || null;
  const prevAvailable = Boolean(prev?.name);
  const isGK          = player?.role === 'P';

  const seasonDailyData = useMemo(() => buildSeasonDailySeries(player), [player]);
  const runningAvgData  = useMemo(() => seasonDailyData.map(d => ({ day: `G${d.day}`, mark: d.mark, fmark: d.fmark })), [seasonDailyData]);
  const lastFiveData    = useMemo(() => buildLastFiveSeries(player) || [], [player]);

  const formJudgment = useMemo(() => buildFormJudgment(lastFiveData), [lastFiveData]);

  const radarData = useMemo(() => {
    if (!player) return [];
    return [
      { stat: 'Gol',      value: Math.min(v(player.goals)   / 2, 10) },
      { stat: 'Assist',   value: Math.min(v(player.assists) / 2, 10) },
      { stat: 'Presenze', value: Math.min(v(player.caps)    / 4, 10) },
      { stat: 'Voto',     value: Math.min(v(player.markavg),    10) },
      { stat: 'FV',       value: Math.min(v(player.fmarkavg),   10) },
    ];
  }, [player]);

  // Radar duale corrente + precedente per tab Stagione prec.
  const radarDualData = useMemo(() => {
    if (!player || !prevAvailable) return [];
    return [
      { stat: 'Gol',      curr: Math.min(v(player.goals)   / 2, 10), prev: Math.min(v(prev.goals)   / 2, 10) },
      { stat: 'Assist',   curr: Math.min(v(player.assists) / 2, 10), prev: Math.min(v(prev.assists) / 2, 10) },
      { stat: 'Presenze', curr: Math.min(v(player.caps)    / 4, 10), prev: Math.min(v(prev.caps)    / 4, 10) },
      { stat: 'Voto',     curr: Math.min(v(player.markavg),    10),  prev: Math.min(v(prev.markavg),    10)  },
      { stat: 'FV',       curr: Math.min(v(player.fmarkavg),   10),  prev: Math.min(v(prev.fmarkavg),   10)  },
    ];
  }, [player, prev, prevAvailable]);

  const heroKpis = useMemo(() => {
    if (!player) return [];
    const items = [
      { icon: '/attribute-icon/gol.png',    label: 'Gol',      value: fmt(player.goals),   fallbackText: 'GF' },
      { icon: '/attribute-icon/assist.png', label: 'Assist',   value: fmt(player.assists), fallbackText: 'AS' },
      { icon: '/attribute-icon/pg.png',     label: 'Presenze', value: fmt(player.caps),    fallbackText: 'PG' },
    ];
    if (isGK) items.push({ icon: '/attribute-icon/golsubito.png',  label: 'Gol subiti', value: fmt(player.gotgoals),  fallbackText: 'GS' });
    else      items.push({ icon: '/attribute-icon/rig_segnato.png', label: 'Rigori',    value: fmt(player.penalties), fallbackText: 'RS' });
    items.push(
      { LucideIcon: Activity,   label: 'Media voto', value: fmt(player.markavg),  accent: PRIMARY   },
      { LucideIcon: TrendingUp, label: 'Media FV',   value: fmt(player.fmarkavg), accent: SECONDARY },
      { LucideIcon: Trophy,     label: 'Valutazione', value: fmt(player.value) },
    );
    return items;
  }, [player, isGK]);

  // Gruppi statistiche per tab Statistiche
  const statGroups = useMemo(() => {
    if (!player) return [];
    const games = Math.max(v(player.caps), 1);
    const groups = [
      {
        title: 'Rendimento', icon: Star, accent: PRIMARY,
        items: [
          { label: 'Media voto',       value: fmtDec(player.markavg),  highlight: true },
          { label: 'Media fanta voto', value: fmtDec(player.fmarkavg), highlight: true },
          { label: 'Valutazione',      value: fmt(player.value) },
          { label: 'Presenze',         value: fmt(player.caps) },
          { label: 'Playmaker',        value: player.playmaker ? 'Sì' : 'No' },
          { label: 'Ruolo',            value: formatRoleLabel(player.role) },
        ],
      },
    ];

    if (!isGK) {
      groups.push({
        title: 'Contributo offensivo', icon: Target, accent: '#f97316',
        items: [
          { label: 'Gol',              value: fmt(player.goals),   highlight: true },
          { label: 'Assist',           value: fmt(player.assists), highlight: true },
          { label: 'Gol / partita',    value: fmtDec(v(player.goals)   / games), sub: `su ${fmt(player.caps)} presenze` },
          { label: 'Assist / partita', value: fmtDec(v(player.assists) / games) },
          { label: 'Rigori segnati',   value: fmt(getPenaltyScored(player)) },
          { label: 'Rigori sbagliati', value: fmt(getPenaltyMissed(player)) },
        ],
      });
    } else {
      groups.push({
        title: 'Rendimento portiere', icon: Shield, accent: SECONDARY,
        items: [
          { label: 'Gol subiti',      value: fmt(player.gotgoals), highlight: true },
          { label: 'Gol sub. / gara', value: fmtDec(v(player.gotgoals) / games), sub: `su ${fmt(player.caps)} presenze` },
          { label: 'Rigori parati',   value: fmt(getPenaltySaved(player)), highlight: true },
          { label: 'Autogol',         value: fmt(player.owngoals) },
        ],
      });
    }

    groups.push({
      title: 'Disciplina', icon: AlertTriangle, accent: WARN,
      items: [
        { label: 'Ammonizioni', value: fmt(player.ycards), highlight: v(player.ycards) >= 5 },
        { label: 'Espulsioni',  value: fmt(player.rcards), highlight: v(player.rcards) >= 1 },
        { label: 'Autogol',     value: fmt(player.owngoals) },
        ...(isGK ? [] : [{ label: 'Rigori calciati', value: fmt(getPenaltyAttempts(player)) }]),
      ],
    });

    return groups;
  }, [player, isGK]);

  // Righe confronto per tab Stagione precedente
  const compareRows = useMemo(() => {
    if (!player || !prevAvailable) return [];
    const games  = Math.max(v(player.caps), 1);
    const pgames = Math.max(v(prev.caps), 1);
    const rows = [
      { label: 'Media voto',       curr: v(player.markavg),  prev: v(prev.markavg),  currFmt: fmtDec(player.markavg),  prevFmt: fmtDec(prev.markavg)  },
      { label: 'Media FV',         curr: v(player.fmarkavg), prev: v(prev.fmarkavg), currFmt: fmtDec(player.fmarkavg), prevFmt: fmtDec(prev.fmarkavg) },
      { label: 'Presenze',         curr: v(player.caps),     prev: v(prev.caps),     currFmt: fmt(player.caps),         prevFmt: fmt(prev.caps)         },
      { label: 'Valutazione',      curr: v(player.value),    prev: v(prev.value),    currFmt: fmt(player.value),        prevFmt: fmt(prev.value)        },
    ];
    if (!isGK) {
      rows.push(
        { label: 'Gol',              curr: v(player.goals),   prev: v(prev.goals),   currFmt: fmt(player.goals),   prevFmt: fmt(prev.goals)   },
        { label: 'Assist',           curr: v(player.assists), prev: v(prev.assists), currFmt: fmt(player.assists), prevFmt: fmt(prev.assists) },
        { label: 'Gol / partita',    curr: v(player.goals)/games,   prev: v(prev.goals)/pgames,   currFmt: fmtDec(v(player.goals)/games),   prevFmt: fmtDec(v(prev.goals)/pgames)   },
        { label: 'Assist / partita', curr: v(player.assists)/games, prev: v(prev.assists)/pgames, currFmt: fmtDec(v(player.assists)/games), prevFmt: fmtDec(v(prev.assists)/pgames) },
      );
    } else {
      rows.push(
        { label: 'Gol subiti',      curr: v(player.gotgoals), prev: v(prev.gotgoals), currFmt: fmt(player.gotgoals), prevFmt: fmt(prev.gotgoals), positiveDir: false },
        { label: 'Gol sub./partita', curr: v(player.gotgoals)/games, prev: v(prev.gotgoals)/pgames, currFmt: fmtDec(v(player.gotgoals)/games), prevFmt: fmtDec(v(prev.gotgoals)/pgames), positiveDir: false },
        { label: 'Rigori parati',   curr: getPenaltySaved(player), prev: getPenaltySaved(prev), currFmt: fmt(getPenaltySaved(player)), prevFmt: fmt(getPenaltySaved(prev)) },
      );
    }
    rows.push(
      { label: 'Ammonizioni', curr: v(player.ycards), prev: v(prev.ycards), currFmt: fmt(player.ycards), prevFmt: fmt(prev.ycards), positiveDir: false },
      { label: 'Espulsioni',  curr: v(player.rcards), prev: v(prev.rcards), currFmt: fmt(player.rcards), prevFmt: fmt(prev.rcards), positiveDir: false },
    );
    return rows;
  }, [player, prev, prevAvailable, isGK]);

  const perfDist = useMemo(() => buildPerformanceDistribution(seasonDailyData), [seasonDailyData]);
  const prevPI   = useMemo(() => prevAvailable ? buildPerformanceIndex(prev) : null, [prev, prevAvailable]);
  const currentPI = useMemo(() => buildPerformanceIndex(player), [player]);

  const currentLast5 = useMemo(() => lastFiveData.map(d => ({
    day: d.day, mark: d.mark, fmark: d.fmark,
    goals: d.goals, assists: d.assists, gotgoals: d.gotgoals, saves: d.saves,
  })), [lastFiveData]);

  const TABS = [
    { id: 'overview',  label: 'Panoramica'      },
    { id: 'stats',     label: 'Statistiche'      },
    { id: 'form',      label: 'Forma recente'    },
    { id: 'calendar',  label: 'Calendario'       },
    { id: 'previous',  label: 'Stagione prec.'   },
  ];

  if (loading || !player) {
    return (
      <main className="container detail-page">
        <TopBar />
        <div className="detail-empty glass">
          {loading ? 'Caricamento giocatore...' : 'Giocatore non trovato.'}
        </div>
      </main>
    );
  }

  return (
    <main className="container detail-page">
      <TopBar />

      {/* ── HERO ── */}
      <section className="detail-hero glass">
        <div className="detail-hero__top">
          <div className="detail-hero__id">
            <TeamBadge team={player.team} size={60} />
            <div>
              <div className="detail-hero__meta">
                <StatChip role={player.role} />
                <span className="detail-team">{player.team}</span>
                {prevAvailable && <span className="detail-season-pill">Prec. {prev.season || PREVIOUS_SEASON}</span>}
              </div>
              <h1>{player.name}</h1>
            </div>
          </div>
          <SyncBadge updatedAt={player.updatedAt} ratingsUpdatedAt={player.ratingsUpdatedAt} previousSeasonSyncedAt={player.previousSeasonSyncedAt} />
        </div>

        <div className="detail-hero__kpis">
          {heroKpis.map(item => (
            <MetricBadge key={item.label} icon={item.icon} LucideIcon={item.LucideIcon} label={item.label} value={item.value} fallbackText={item.fallbackText} accent={item.accent} />
          ))}
        </div>
      </section>

      {/* ── TAB BAR ── */}
      <nav className="detail-tabs glass" aria-label="Sezioni giocatore">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={tab === t.id ? 'active' : ''}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* ════ TAB: PANORAMICA ════ */}
      {tab === 'overview' && (
        <section className="detail-grid detail-grid--single">
          <PlayerOverviewCard player={player} prev={prevAvailable ? prev : null} lastFive={lastFiveData} />
        </section>
      )}

      {/* ════ TAB: STATISTICHE ════ */}
      {tab === 'stats' && (
        <section className="detail-grid">

          {/* ── KPI BAR TOP ── */}
          <div className="stats-kpi-bar detail-card--wide glass">
            <div className="stats-kpi-item">
              <span>Media voto</span>
              <strong style={{ color: PRIMARY }}>{fmtDec(player.markavg)}</strong>
            </div>
            <div className="stats-kpi-sep" />
            <div className="stats-kpi-item">
              <span>Media FV</span>
              <strong style={{ color: SECONDARY }}>{fmtDec(player.fmarkavg)}</strong>
            </div>
            <div className="stats-kpi-sep" />
            <div className="stats-kpi-item">
              <span>Presenze</span>
              <strong>{fmt(player.caps)}</strong>
            </div>
            <div className="stats-kpi-sep" />
            {!isGK ? (
              <>
                <div className="stats-kpi-item">
                  <span>Gol</span>
                  <strong style={{ color: '#f97316' }}>{fmt(player.goals)}</strong>
                </div>
                <div className="stats-kpi-sep" />
                <div className="stats-kpi-item">
                  <span>Assist</span>
                  <strong>{fmt(player.assists)}</strong>
                </div>
              </>
            ) : (
              <>
                <div className="stats-kpi-item">
                  <span>Gol subiti</span>
                  <strong style={{ color: DANGER }}>{fmt(player.gotgoals)}</strong>
                </div>
                <div className="stats-kpi-sep" />
                <div className="stats-kpi-item">
                  <span>Rig. parati</span>
                  <strong style={{ color: SECONDARY }}>{fmt(getPenaltySaved(player))}</strong>
                </div>
              </>
            )}
            <div className="stats-kpi-sep" />
            <div className="stats-kpi-item stats-kpi-item--pi">
              <span>Performance index</span>
              <strong>{currentPI.score} <PIBadge label={currentPI.label} /></strong>
            </div>
          </div>

          {/* ── GRUPPI STAT + RADAR affiancati ── */}
          <article className="detail-card glass">
            <div className="detail-card__head">
              <div><h2>Statistiche per area</h2><span className="detail-card__subtitle--hideMobile">Raggruppate per categoria</span></div>
              <div className="detail-card__pill detail-card__pill--hideMobile"><BarChart3 size={16} /><span>Breakdown</span></div>
            </div>
            <div className="stat-groups-list">
              {statGroups.map(g => <StatGroup key={g.title} {...g} />)}
            </div>
          </article>

          <article className="detail-card glass">
            <div className="detail-card__head">
              <div><h2>Profilo radar</h2><span>5 dimensioni normalizzate su 10</span></div>
              <div className="detail-card__pill"><Activity size={16} /><span>Radar</span></div>
            </div>
            <div className="chart-box" style={{ minHeight: 300 }}>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke={gridColor} />
                  <PolarAngleAxis dataKey="stat" tick={{ fill: chartText, fontSize: 12, fontWeight: 700 }} />
                  <PolarRadiusAxis tick={{ fill: chartText, fontSize: 10 }} angle={25} domain={[0, 10]} tickCount={3} />
                  <Tooltip content={<ChartTooltip isDark={isDark} />} />
                  <Radar dataKey="value" stroke={SECONDARY} fill={PRIMARY} fillOpacity={0.28} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </article>

          {/* ── AREA CHART STAGIONE ── */}
          <article className="detail-card glass detail-card--wide">
            <div className="detail-card__head">
              <div><h2>Andamento stagionale</h2><span>Media voto e FV giornata per giornata</span></div>
              <div className="detail-card__pill"><TrendingUp size={16} /><span>Season trend</span></div>
            </div>
            <div className="chart-box chart-box--big">
              {runningAvgData.length ? (
                <ResponsiveContainer width="100%" height={360}>
                  <AreaChart data={runningAvgData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradMark" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={PRIMARY}   stopOpacity={0.2} />
                        <stop offset="95%" stopColor={PRIMARY}   stopOpacity={0}   />
                      </linearGradient>
                      <linearGradient id="gradFmark" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={SECONDARY} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={SECONDARY} stopOpacity={0}   />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                    <XAxis dataKey="day" tick={{ fill: chartText, fontSize: 11 }} interval={3} />
                    <YAxis tick={{ fill: chartText, fontSize: 11 }} domain={[0, 10]} />
                    <ReferenceLine y={6} stroke={gridColor} strokeDasharray="6 3" label={{ value: 'soglia 6', fill: chartText, fontSize: 10, position: 'insideTopRight' }} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
                    <Area type="monotone" dataKey="mark"  name="Media voto"       stroke={PRIMARY}   fill="url(#gradMark)"  strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                    <Area type="monotone" dataKey="fmark" name="Media fanta voto" stroke={SECONDARY} fill="url(#gradFmark)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                    <Brush dataKey="day" height={22} stroke={gridColor} travellerWidth={8} fill={isDark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.04)'} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="detail-empty-inline">Nessuno storico giornate disponibile.</div>
              )}
            </div>
          </article>

          {/* ── DISTRIBUZIONE VOTI ── */}
          <article className="detail-card glass detail-card--wide">
            <div className="detail-card__head">
              <div><h2>Distribuzione dei voti</h2><span>Quante giornate per fascia di rendimento</span></div>
              <div className="detail-card__pill"><BarChart3 size={16} /><span>Distribution</span></div>
            </div>
            <div className="chart-box chart-box--big">
              {perfDist.some(d => d.count > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={perfDist} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                    <XAxis dataKey="range" tick={{ fill: chartText, fontSize: 12, fontWeight: 700 }} />
                    <YAxis tick={{ fill: chartText, fontSize: 11 }} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Bar dataKey="count" name="Giornate" radius={[8, 8, 0, 0]}>
                      {perfDist.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="detail-empty-inline">Nessuna distribuzione disponibile.</div>
              )}
            </div>
            {/* leggenda colori */}
            <div className="dist-legend">
              {perfDist.map(b => (
                <div key={b.range} className="dist-legend__item">
                  <span className="dist-legend__dot" style={{ background: b.color }} />
                  <span>{b.range}</span>
                  <strong>{b.count}</strong>
                </div>
              ))}
            </div>
          </article>

        </section>
      )}

      {/* ════ TAB: FORMA RECENTE ════ */}
      {tab === 'form' && (
        <section className="detail-grid">

          {/* ── BANNER FORMA ── */}
          {formJudgment && (
            <div className={`form-banner form-banner--${formJudgment.cls} detail-card--wide`}>
              <div className="form-banner__left">
                <span className="form-banner__eyebrow">Stato di forma</span>
                <strong className="form-banner__label">{formJudgment.label}</strong>
                <p className="form-banner__desc">{formJudgment.description}</p>
              </div>
              <div className="form-banner__stats">
                <div className="form-banner__stat">
                  <span>Media FV ult. 5</span>
                  <strong>{fmtDec(formJudgment.avg)}</strong>
                </div>
                <div className="form-banner__stat">
                  <span>Trend ultima</span>
                  <strong className={formJudgment.trend >= 0 ? 'text--up' : 'text--down'}>
                    {formJudgment.trend >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    {fmtDec(Math.abs(formJudgment.trend))}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* ── KPI SOMMARIO ── */}
          <article className="detail-card glass">
            <div className="detail-card__head">
              <div><h2>Sommario ultime 5</h2><span className="detail-card__subtitle--hideMobile">Aggregati del periodo recente</span></div>
              <div className="detail-card__pill detail-card__pill--hideMobile"><Flame size={16} /><span>Summary</span></div>
            </div>
            <div className="form-summary-grid">
              <div className="form-kpi">
                <span>MV media</span>
                <strong>{lastFiveData.length ? fmtDec(lastFiveData.reduce((a, d) => a + v(d.mark),  0) / lastFiveData.length) : '—'}</strong>
              </div>
              <div className="form-kpi form-kpi--accent">
                <span>FV media</span>
                <strong>{lastFiveData.length ? fmtDec(lastFiveData.reduce((a, d) => a + v(d.fmark), 0) / lastFiveData.length) : '—'}</strong>
              </div>
              <div className="form-kpi">
                <span>Gol segnati</span>
                <strong>{fmt(currentLast5.reduce((a, d) => a + v(d.goals),   0))}</strong>
              </div>
              <div className="form-kpi">
                <span>Assist</span>
                <strong>{fmt(currentLast5.reduce((a, d) => a + v(d.assists), 0))}</strong>
              </div>
              {isGK && (
                <>
                  <div className="form-kpi">
                    <span>Gol subiti</span>
                    <strong>{fmt(currentLast5.reduce((a, d) => a + v(d.gotgoals), 0))}</strong>
                  </div>
                  <div className="form-kpi form-kpi--save">
                    <span>Rig. parati</span>
                    <strong>{fmt(currentLast5.reduce((a, d) => a + v(d.saves), 0))}</strong>
                  </div>
                </>
              )}
            </div>
          </article>

          {/* ── LINE CHART ── */}
          <article className="detail-card glass">
            <div className="detail-card__head">
              <div><h2>Andamento ultime 5</h2><span>Voto e fanta voto per giornata</span></div>
            </div>
            <div className="chart-box chart-box--big">
              {currentLast5.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={currentLast5.map(d => ({ ...d, day: `G${d.day}` }))} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                    <XAxis dataKey="day" tick={{ fill: chartText, fontSize: 12, fontWeight: 700 }} />
                    <YAxis tick={{ fill: chartText, fontSize: 11 }} domain={[0, 10]} />
                    <ReferenceLine y={6} stroke={gridColor} strokeDasharray="6 3" />
                    <Tooltip content={<ChartTooltip isDark={isDark} />} />
                    <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
                    <Line type="monotone" dataKey="mark"  name="Voto"       stroke={PRIMARY}   strokeWidth={3} dot={{ r: 5, fill: PRIMARY,   stroke: 'var(--card)', strokeWidth: 2 }} activeDot={{ r: 7 }} />
                    <Line type="monotone" dataKey="fmark" name="Fanta voto" stroke={SECONDARY} strokeWidth={3} dot={{ r: 5, fill: SECONDARY, stroke: 'var(--card)', strokeWidth: 2 }} activeDot={{ r: 7 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="detail-empty-inline">Nessuna forma recente disponibile.</div>
              )}
            </div>
          </article>

          {/* ── TIMELINE VERTICALE ── */}
          <article className="detail-card glass detail-card--wide">
            <div className="detail-card__head">
              <div><h2>Giornata per giornata</h2><span>Dettaglio di ogni partita delle ultime 5 giornate</span></div>
            </div>
            {currentLast5.length ? (
              <div className="forma-timeline">
                {currentLast5.map((item, i) => (
                  <FormaDayCard key={item.day ?? i} item={item} index={i} />
                ))}
              </div>
            ) : (
              <div className="detail-empty-inline">Nessun dato disponibile.</div>
            )}
          </article>

        </section>
      )}

      {/* ════ TAB: CALENDARIO ════ */}
      {tab === 'calendar' && <CalendarTab player={player} />}

      {/* ════ TAB: STAGIONE PRECEDENTE ════ */}
      {tab === 'previous' && (
        <section className="detail-grid">

          {!prevAvailable ? (
            <article className="detail-card glass detail-card--wide">
              <div className="detail-card__head">
                <div><h2>Stagione precedente</h2></div>
                <div className="detail-card__pill"><Trophy size={16} /><span>Nessun dato</span></div>
              </div>
              <div className="detail-empty-inline">Nessun match affidabile trovato nella stagione precedente.</div>
            </article>
          ) : (
            <>
              {/* ── HEADER CONFRONTO ── */}
              <div className="prev-header detail-card--wide glass">
                <div className="prev-header__side prev-header__side--curr">
                  <span className="prev-header__badge prev-header__badge--curr">Stagione corrente</span>
                  <TeamBadge team={player.team} size={48} />
                  <strong>{player.name}</strong>
                  <span>{player.team} · {formatRoleLabel(player.role)}</span>
                  <div className="prev-header__stats">
                    <div><span>MV</span><strong style={{ color: PRIMARY }}>{fmtDec(player.markavg)}</strong></div>
                    <div><span>FV</span><strong style={{ color: SECONDARY }}>{fmtDec(player.fmarkavg)}</strong></div>
                    <div><span>PI</span><strong>{currentPI.score} <PIBadge label={currentPI.label} /></strong></div>
                  </div>
                </div>
                <div className="prev-header__vs">VS</div>
                <div className="prev-header__side prev-header__side--prev">
                  <span className="prev-header__badge prev-header__badge--prev">Stagione {prev.season || PREVIOUS_SEASON}</span>
                  <TeamBadge team={prev.team} size={48} />
                  <strong>{prev.name}</strong>
                  <span>{prev.team} · {formatRoleLabel(prev.role)}</span>
                  <div className="prev-header__stats">
                    <div><span>MV</span><strong style={{ color: PRIMARY }}>{fmtDec(prev.markavg)}</strong></div>
                    <div><span>FV</span><strong style={{ color: SECONDARY }}>{fmtDec(prev.fmarkavg)}</strong></div>
                    {prevPI && <div><span>PI</span><strong>{prevPI.score} <PIBadge label={prevPI.label} /></strong></div>}
                  </div>
                </div>
              </div>

              {/* ── CONFRONTO TABELLA + RADAR DUALE ── */}
              <article className="detail-card glass">
                <div className="detail-card__head">
                  <div><h2>Confronto metrica per metrica</h2><span>Corrente vs precedente con delta</span></div>
                  <div className="detail-card__pill"><TrendingUp size={16} /><span>Head-to-head</span></div>
                </div>
                <CompareTable rows={compareRows} />
              </article>

              <article className="detail-card glass">
                <div className="detail-card__head">
                  <div><h2>Radar sovrapposto</h2><span>Confronto visivo delle 5 dimensioni chiave</span></div>
                  <div className="detail-card__pill"><Activity size={16} /><span>Dual radar</span></div>
                </div>
                <div className="chart-box" style={{ minHeight: 300 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarDualData} outerRadius="72%">
                      <PolarGrid stroke={gridColor} />
                      <PolarAngleAxis dataKey="stat" tick={{ fill: chartText, fontSize: 12, fontWeight: 700 }} />
                      <PolarRadiusAxis tick={{ fill: chartText, fontSize: 10 }} angle={25} domain={[0, 10]} tickCount={3} />
                      <Tooltip content={<ChartTooltip isDark={isDark} />} />
                      <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
                      <Radar name="Corrente"   dataKey="curr" stroke={PRIMARY}   fill={PRIMARY}   fillOpacity={0.22} strokeWidth={2} />
                      <Radar name="Precedente" dataKey="prev" stroke={SECONDARY} fill={SECONDARY} fillOpacity={0.15} strokeWidth={2} strokeDasharray="5 3" />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </article>

              {/* ── CHART ANDAMENTO CORRENTE VS FV PRECEDENTE ── */}
              <article className="detail-card glass detail-card--wide">
                <div className="detail-card__head">
                  <div><h2>MV stagione corrente giornata per giornata</h2><span>Con linea di riferimento sulla media stagione precedente</span></div>
                  <div className="detail-card__pill"><BarChart3 size={16} /><span>Season bars</span></div>
                </div>
                <div className="chart-box chart-box--big">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={seasonDailyData.map(d => ({ name: `G${d.day}`, mark: d.mark, fmark: d.fmark }))}
                      margin={{ top: 10, right: 12, left: -8, bottom: 0 }}
                    >
                      <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                      <XAxis dataKey="name" tick={{ fill: chartText, fontSize: 11 }} interval={3} />
                      <YAxis tick={{ fill: chartText, fontSize: 11 }} domain={[0, 10]} />
                      <ReferenceLine
                        y={v(prev.markavg)}
                        stroke={SECONDARY}
                        strokeDasharray="6 3"
                        label={{ value: `MV prec. ${fmtDec(prev.markavg)}`, fill: SECONDARY, fontSize: 11, position: 'insideTopRight' }}
                      />
                      <ReferenceLine
                        y={v(prev.fmarkavg)}
                        stroke={PRIMARY}
                        strokeDasharray="6 3"
                        label={{ value: `FV prec. ${fmtDec(prev.fmarkavg)}`, fill: PRIMARY, fontSize: 11, position: 'insideBottomRight' }}
                      />
                      <Tooltip content={<ChartTooltip isDark={isDark} />} />
                      <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
                      <Bar dataKey="mark"  name="MV corrente" fill={PRIMARY}   radius={[5, 5, 0, 0]} opacity={0.85} />
                      <Bar dataKey="fmark" name="FV corrente" fill={SECONDARY} radius={[5, 5, 0, 0]} opacity={0.85} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </article>

              {/* ── PI BLOCK STAGIONE PREC ── */}
              {prevPI && (
                <article className="detail-card glass detail-card--wide">
                  <div className="detail-card__head">
                    <div><h2>Performance index stagione precedente</h2><span>Componenti dell'indice sintetico 0–100</span></div>
                    <div className="detail-card__pill"><Activity size={16} /><span>{prevPI.label}</span></div>
                  </div>
                  <div className="detail-pi-block">
                    <div className="detail-pi-block__left">
                      <span className="detail-pi-block__sublabel">Indice sintetico</span>
                      <strong className="detail-pi-block__score">{prevPI.score}</strong>
                      <PIBadge label={prevPI.label} />
                    </div>
                    <div className="detail-pi-block__bar-wrap">
                      <div className="detail-pi-block__track">
                        <div className="detail-pi-block__fill" style={{ width: `${prevPI.score}%` }} />
                      </div>
                      <div className="detail-pi-block__range"><span>0</span><span>100</span></div>
                    </div>
                  </div>
                  <div className="pi-components-grid">
                    {prevPI.components.map(item => (
                      <MetricBadge key={item.label} icon={item.icon} label={item.label} value={fmt(item.value)} fallbackText={item.fallbackText} />
                    ))}
                  </div>
                </article>
              )}
            </>
          )}
        </section>
      )}

      <Link className="back-link" to="/players">
        <ArrowLeft size={14} /> Torna alla lista
      </Link>
    </main>
  );
}
